import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight, CalendarDays, MapPin, ChevronDown, Images, Instagram, Mail, Sparkles,
} from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AspectRatio } from '../components/ui/aspect-ratio';
import { Skeleton } from '../components/ui/skeleton';
import {
  Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext,
} from '../components/ui/carousel';
import { ThemeToggle } from '../components/landing/ThemeToggle';
import { usePublicTheme } from '../hooks/usePublicTheme';
import { getFontTheme, ensureFontLoaded } from '../lib/fontThemes';
import { formatDateLong, formatTime12, zoneAbbr } from '../lib/time';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const img = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);

const HEX_RE = /^#([0-9a-fA-F]{6})$/;
const DEFAULT_SECTIONS = [
  { id: 'events', visible: true },
  { id: 'gallery', visible: true },
  { id: 'about', visible: true },
];

// Convert #rrggbb to the "H S% L%" channel string Tailwind expects in hsl(var(--x)).
const hexToHsl = (hex) => {
  if (!HEX_RE.test(hex || '')) return null;
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const setMetaTag = (attr, key, content) => {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content || '');
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

export default function Landing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reduce = useReducedMotion();
  const { theme, toggle } = usePublicTheme(data?.default_theme);

  useEffect(() => {
    api.get('/public/landing').then((r) => setData(r.data)).catch(() => setData({})).finally(() => setLoading(false));
  }, []);

  const hero_title = data?.hero_title || 'Where good people gather.';
  const hero_subtitle = data?.hero_subtitle || 'Unforgettable events, parties, and gatherings — hosted by Thrive Gatherings.';
  const events = data?.featured_events || [];
  const photos = data?.site_photos || [];
  const ft = getFontTheme(data?.font_theme);
  const fontVars = { '--tg-font-display': ft.display, '--tg-font-body': ft.body };
  ensureFontLoaded(data?.font_theme);

  const brandVars = {};
  const bp = hexToHsl(data?.brand_primary);
  const ba = hexToHsl(data?.brand_accent);
  if (bp) { brandVars['--primary'] = bp; brandVars['--ring'] = bp; }
  if (ba) { brandVars['--accent'] = ba; }
  const rootStyle = { ...fontVars, ...brandVars };

  const sectionList = (data?.sections && data.sections.length ? data.sections : DEFAULT_SECTIONS);

  useEffect(() => {
    if (!data) return;
    const title = data.seo_title || 'Thrive Gatherings';
    document.title = title;
    setMetaTag('name', 'description', data.seo_description || '');
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', data.seo_description || '');
    setMetaTag('property', 'og:type', 'website');
    if (data.og_image_url) setMetaTag('property', 'og:image', img(data.og_image_url));
    setMetaTag('name', 'twitter:card', data.og_image_url ? 'summary_large_image' : 'summary');
  }, [data]);

  const renderSection = (id) => {
    if (id === 'events') {
      return (
        <Section id="events" eyebrow="What's coming up" title="Featured Events" testid="public-featured-events-section">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-80 rounded-xl bg-muted" />)}
            </div>
          ) : events.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center" data-testid="public-featured-events-empty">
              <div className="h-12 w-12 rounded-2xl bg-primary/10 mx-auto flex items-center justify-center mb-4"><CalendarDays className="h-6 w-6 text-primary" /></div>
              <h3 className="font-display text-xl text-foreground">New events dropping soon</h3>
              <p className="text-muted-foreground mt-1.5 text-sm">Check back shortly — something special is in the works.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {events.map((ev) => <EventFlyer key={ev.id} ev={ev} />)}
            </div>
          )}
        </Section>
      );
    }
    if (id === 'gallery') {
      return (
        <section id="gallery" className="py-14 sm:py-20" data-testid="public-highlights-carousel-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-7">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Moments worth reliving</div>
            <h2 className="font-display text-2xl sm:text-3xl text-foreground mt-1">Highlights</h2>
          </div>
          {loading ? (
            <div className="max-w-6xl mx-auto px-4 sm:px-6"><Skeleton className="h-72 sm:h-96 rounded-2xl bg-muted" /></div>
          ) : photos.length === 0 ? (
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
                <div className="h-12 w-12 rounded-2xl bg-primary/10 mx-auto flex items-center justify-center mb-4"><Images className="h-6 w-6 text-primary" /></div>
                <h3 className="font-display text-xl text-foreground">Highlights coming soon</h3>
                <p className="text-muted-foreground mt-1.5 text-sm">Follow Thrive Gatherings to relive the best moments.</p>
              </div>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <Carousel opts={{ loop: photos.length > 1, align: 'start' }} className="w-full" data-testid="public-highlights-carousel">
                <CarouselContent>
                  {photos.map((p) => (
                    <CarouselItem key={p.id} className="md:basis-4/5 lg:basis-3/4" data-testid="public-highlights-slide">
                      <div className="relative rounded-2xl overflow-hidden border border-border">
                        <AspectRatio ratio={16 / 9}>
                          <img src={img(p.url)} alt={p.caption || 'Event highlight'} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                        </AspectRatio>
                        {p.caption && (
                          <div className="absolute bottom-3 left-3 px-3 py-1.5 rounded-full bg-black/45 backdrop-blur border border-white/10 text-sm text-white">{p.caption}</div>
                        )}
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="h-10 w-10 rounded-full bg-card/70 border-border text-foreground hover:bg-card" data-testid="public-highlights-prev" />
                <CarouselNext className="h-10 w-10 rounded-full bg-card/70 border-border text-foreground hover:bg-card" data-testid="public-highlights-next" />
              </Carousel>
            </div>
          )}
        </section>
      );
    }
    if (id === 'about') {
      return (
        <Section id="about" eyebrow="Our story" title="About Thrive Gatherings" testid="public-about-section">
          <div className="rounded-2xl bg-card border border-border p-6 sm:p-10">
            <p className="text-muted-foreground leading-relaxed text-base sm:text-lg max-w-3xl whitespace-pre-line">
              {data?.about || 'Thrive Gatherings brings people together for unforgettable parties, mixers, and community events. From intimate dinners to big celebrations, we craft experiences where everyone feels welcome — and nobody wants to leave early.'}
            </p>
          </div>
        </Section>
      );
    }
    return null;
  };

  return (
    <div className="tg-public tg-fontscope tg-noise min-h-screen bg-background text-foreground" style={rootStyle} data-theme={theme} data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border" data-testid="public-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="#top" onClick={(e) => { e.preventDefault(); scrollTo('top'); }} className="flex items-center gap-2.5">
            <img src="/thrive-logo.png" alt="Thrive Gatherings" className="h-9 w-9 rounded-full object-cover" />
            <span className="font-display text-lg font-semibold text-foreground tracking-tight">Thrive Gatherings</span>
          </a>
          <nav className="flex items-center gap-4 sm:gap-5">
            <button onClick={() => scrollTo('events')} className="hidden sm:block text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="public-nav-events-link">Events</button>
            <button onClick={() => scrollTo('gallery')} className="hidden sm:block text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="public-nav-gallery-link">Gallery</button>
            <Link to="/login" className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground transition-colors" data-testid="public-host-login-link">Host Login</Link>
            <ThemeToggle theme={theme} onToggle={toggle} />
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none tg-hero-glow" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-16 sm:pt-28 sm:pb-20 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card text-xs uppercase tracking-[0.18em] text-muted-foreground mb-6">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> Hosted by Thrive Gatherings
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] text-foreground leading-[1.04]" data-testid="public-hero-headline">
              {hero_title}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed" data-testid="public-hero-tagline">
              {hero_subtitle}
            </p>
            <div className="mt-8 flex items-center gap-3">
              <Button onClick={() => scrollTo('events')} className="h-12 px-7 gap-2 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]" data-testid="public-hero-cta-events">
                View Events <ArrowRight className="h-4 w-4" />
              </Button>
              <Button onClick={() => scrollTo('gallery')} variant="outline" className="h-12 px-7 gap-2 bg-transparent text-foreground border-border hover:bg-muted" data-testid="public-hero-cta-gallery">
                <Images className="h-4 w-4" /> See Highlights
              </Button>
            </div>
          </motion.div>

          <div className="flex justify-center lg:justify-end">
            <motion.div
              animate={reduce ? {} : { y: [0, -8, 0] }}
              transition={{ duration: 6, ease: 'easeInOut', repeat: Infinity }}
              className="relative"
            >
              <div className="absolute inset-0 rounded-full blur-3xl opacity-60" style={{ background: 'radial-gradient(circle, rgba(45,107,224,0.45), rgba(43,183,196,0.25) 55%, transparent 70%)' }} />
              <img src="/thrive-logo.png" alt="Thrive Gatherings logo" className="relative h-56 w-56 sm:h-72 sm:w-72 rounded-full object-cover ring-1 ring-border" />
            </motion.div>
          </div>
        </div>
        <button onClick={() => scrollTo('events')} className="relative mx-auto mb-8 flex flex-col items-center text-muted-foreground hover:text-foreground transition-colors" aria-label="Scroll to events">
          {!reduce ? <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}><ChevronDown className="h-5 w-5" /></motion.span> : <ChevronDown className="h-5 w-5" />}
        </button>
      </section>

      {sectionList.filter((s) => s.visible).map((s) => (
        <React.Fragment key={s.id}>{renderSection(s.id)}</React.Fragment>
      ))}

      {/* Footer */}
      <footer className="border-t border-border py-10" data-testid="public-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/thrive-logo.png" alt="" className="h-8 w-8 rounded-full object-cover" />
            <span className="font-display text-foreground">Thrive Gatherings</span>
          </div>
          <div className="flex items-center gap-5 text-muted-foreground">
            {data?.instagram && <a href={data.instagram} target="_blank" rel="noreferrer" className="hover:text-foreground transition-colors flex items-center gap-1.5"><Instagram className="h-4 w-4" /> Instagram</a>}
            {data?.contact_email && <a href={`mailto:${data.contact_email}`} className="hover:text-foreground transition-colors flex items-center gap-1.5"><Mail className="h-4 w-4" /> Contact</a>}
            <Link to="/login" className="text-xs uppercase tracking-[0.18em] hover:text-foreground transition-colors" data-testid="public-footer-host-login-link">Host Login</Link>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-6 text-xs text-muted-foreground/70">© {new Date().getFullYear()} Thrive Gatherings · thrivegathering.life</div>
      </footer>
    </div>
  );
}

const Section = ({ id, eyebrow, title, testid, children }) => (
  <section id={id} className="py-14 sm:py-20" data-testid={testid}>
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="mb-7">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{eyebrow}</div>
        <h2 className="font-display text-2xl sm:text-3xl text-foreground mt-1">{title}</h2>
      </div>
      {children}
    </div>
  </section>
);

const EventFlyer = ({ ev }) => {
  const time12 = formatTime12(ev.time);
  const when = [ev.date ? formatDateLong(ev.date) : '', time12 ? `${time12} ${zoneAbbr(ev.timezone)}` : ''].filter(Boolean).join(' · ');
  const where = ev.location_name;
  return (
    <Link to={`/e/${ev.slug}`} className="group block rounded-xl overflow-hidden bg-card border border-border hover:border-primary/40 transition-colors shadow-[0_10px_40px_rgba(0,0,0,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="public-featured-event-card">
      <div className="relative">
        <AspectRatio ratio={3 / 4}>
          {ev.cover_image
            ? <img src={img(ev.cover_image)} alt={ev.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            : <div className="h-full w-full" style={{ background: `linear-gradient(150deg, ${ev.theme_color || '#1E63D6'}, ${ev.accent_color || '#2BB7C4'})` }} />}
        </AspectRatio>
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-display text-xl font-semibold text-white leading-tight">{ev.title}</h3>
          {ev.host && <p className="text-white/70 text-sm mt-0.5">Hosted by {ev.host}</p>}
        </div>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap gap-2 mb-3">
          {when && <Badge className="bg-muted text-muted-foreground border border-border font-normal gap-1 hover:bg-muted"><CalendarDays className="h-3 w-3" /> {when}</Badge>}
          {where && <Badge className="bg-muted text-muted-foreground border border-border font-normal gap-1 hover:bg-muted"><MapPin className="h-3 w-3" /> {where}</Badge>}
        </div>
        {ev.description && <p className="text-sm text-muted-foreground line-clamp-2">{ev.description}</p>}
        <div className="mt-3 flex items-center gap-1 text-sm font-medium text-primary">
          View details <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
  );
};
